/**
 * GlacierX — Immersive Landing Page Interactions
 * v3: reveal repeatable, scene active toggle, sticky pin
 */

document.addEventListener('DOMContentLoaded', () => {
  /* ====== SUPPRESS STORE-WIDE POPUPS ON THIS STANDALONE PAGE ====== */
  const storePopupSelector = [
    '[class*="ecomsend__"]',
    '[id*="ecomsend"]',
    '.react-responsive-modal-root',
    'iframe[src*="ecomsend"]',
    'iframe[title*="EcomSend"]'
  ].join(',');
  const suppressStorePopups = () => {
    document.querySelectorAll(storePopupSelector).forEach((element) => {
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    });
    document.body.style.removeProperty('overflow');
  };
  suppressStorePopups();
  new MutationObserver(suppressStorePopups).observe(document.body, {
    childList: true,
    subtree: true
  });

  /* ====== NAV SCROLL ====== */
  const nav = document.getElementById('nav');
  window.addEventListener('scroll', () => {
    nav.classList.toggle('scrolled', window.scrollY > 60);
  }, { passive: true });

  /* ====== HERO LOAD SEQUENCE ====== */
  requestAnimationFrame(() => {
    document.querySelectorAll('#hero .reveal').forEach(el => el.classList.add('visible'));
  });

  /* ====== REVEAL ON SCROLL (可重复触发) ====== */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.target.closest('#hero')) {
        entry.target.classList.toggle('visible', entry.isIntersecting);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -20px 0px' });

  document.querySelectorAll('.reveal').forEach(el => {
    if (!el.closest('#hero')) revealObserver.observe(el);
  });

  /* ====== MEET GLACIER X VIDEO CONTROLS ====== */
  document.querySelectorAll('[data-demo-player]').forEach(player => {
    const video = player.querySelector('video');
    const toggle = player.querySelector('[data-demo-toggle]');
    const seek = player.querySelector('[data-demo-seek]');
    const current = player.querySelector('[data-demo-current]');
    const duration = player.querySelector('[data-demo-duration]');
    const mute = player.querySelector('[data-demo-mute]');
    const sound = player.querySelector('[data-demo-sound]');
    const speed = player.querySelector('[data-demo-speed]');
    const fullscreen = player.querySelector('[data-demo-fullscreen]');
    if (!video || !toggle || !seek || !current || !duration || !mute || !sound || !speed || !fullscreen) return;

    const formatTime = seconds => {
      if (!Number.isFinite(seconds)) return '0:00';
      const total = Math.floor(seconds);
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    };
    const updateTime = () => {
      const length = video.duration;
      const canSeek = Number.isFinite(length) && length > 0;
      seek.disabled = !canSeek;
      seek.max = canSeek ? String(length) : '100';
      seek.value = canSeek ? String(Math.min(video.currentTime, length)) : '0';
      seek.style.setProperty('--progress', canSeek ? `${video.currentTime / length * 100}%` : '0%');
      current.textContent = formatTime(video.currentTime);
      duration.textContent = formatTime(length);
    };
    const updatePlayback = () => {
      player.classList.toggle('is-paused', video.paused);
      toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    };
    const updateSound = () => {
      mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      sound.textContent = video.muted ? 'Unmute' : 'Mute';
    };
    const updateFullscreen = () => {
      const active = document.fullscreenElement === player || document.webkitFullscreenElement === player;
      player.classList.toggle('is-fullscreen', active);
      fullscreen.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
    };
    const nativeVideoFullscreen = () => {
      video.controls = true;
      try { video.webkitEnterFullscreen(); }
      catch (_) { video.controls = false; }
    };

    toggle.addEventListener('click', () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    seek.addEventListener('input', () => {
      video.currentTime = Number(seek.value);
      updateTime();
    });
    mute.addEventListener('click', () => { video.muted = !video.muted; });
    speed.addEventListener('change', () => { video.playbackRate = Number(speed.value); });
    fullscreen.addEventListener('click', () => {
      const active = document.fullscreenElement === player || document.webkitFullscreenElement === player;
      if (active) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else if (document.fullscreenEnabled && player.requestFullscreen) {
        player.requestFullscreen().catch(() => {});
      } else if (document.webkitFullscreenEnabled && player.webkitRequestFullscreen) {
        player.webkitRequestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        nativeVideoFullscreen();
      }
    });
    video.addEventListener('play', updatePlayback);
    video.addEventListener('pause', updatePlayback);
    video.addEventListener('volumechange', updateSound);
    video.addEventListener('ratechange', () => { speed.value = String(video.playbackRate); });
    video.addEventListener('webkitendfullscreen', () => {
      video.controls = !player.classList.contains('is-ready');
      updateFullscreen();
    });
    document.addEventListener('fullscreenchange', updateFullscreen);
    document.addEventListener('webkitfullscreenchange', updateFullscreen);
    video.addEventListener('loadedmetadata', updateTime);
    video.addEventListener('durationchange', updateTime);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('error', () => {
      player.classList.remove('is-ready');
      video.controls = true;
    });

    updateTime();
    updatePlayback();
    updateSound();
    speed.value = String(video.playbackRate);
    fullscreen.hidden = !((document.fullscreenEnabled && player.requestFullscreen) || (document.webkitFullscreenEnabled && player.webkitRequestFullscreen) || video.webkitEnterFullscreen);
    updateFullscreen();
    video.controls = false;
    player.classList.add('is-ready');
  });

  /* ====== SCENE ACTIVE TOGGLE ====== */
  const sceneObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      entry.target.classList.toggle('scene-active', entry.isIntersecting);
    });
  }, { threshold: 0.2 });

  document.querySelectorAll('.scene').forEach(scene => sceneObserver.observe(scene));

  /* ====== COUNT-UP (一次性 — 数字滚动到目标值后不再变) ====== */
  const countObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseInt(el.getAttribute('data-target'), 10);
      if (isNaN(target)) return;
      const duration = 1800;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const e = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * e).toLocaleString('en-US');
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = target.toLocaleString('en-US');
      };
      requestAnimationFrame(tick);
      countObserver.unobserve(el);
    });
  }, { threshold: 0.4 });
  document.querySelectorAll('.count-up').forEach(el => countObserver.observe(el));

  /* ====== DPI LED PREVIEW ====== */
  const dpiPreview = document.getElementById('dpiPreview');
  const dpiPreviewImage = document.getElementById('dpiPreviewImage');
  const dpiLeds = document.querySelectorAll('.dpi-led');
  const setDpiPreview = (led) => {
    if (!dpiPreview || !dpiPreviewImage) return;
    dpiPreviewImage.src = led.dataset.image;
    dpiPreviewImage.alt = led.dataset.alt;
    dpiLeds.forEach(item => {
      const active = item === led;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
  };
  dpiLeds.forEach(led => {
    led.addEventListener('mouseenter', () => setDpiPreview(led));
    led.addEventListener('focus', () => setDpiPreview(led));
    led.addEventListener('click', () => setDpiPreview(led));
  });

  /* ====== PRESET VIDEO TABS ====== */
  const presetPreview = document.getElementById('presetPreview');
  const presetVideo = document.getElementById('presetPreviewVideo');
  const presetLoading = presetPreview?.querySelector('.preset-loading');
  const presetCards = [...document.querySelectorAll('.preset-card[data-preset-video]')];
  const showPresetLoading = () => {
    if (!presetPreview) return;
    presetPreview.classList.add('is-loading');
    presetPreview.setAttribute('aria-busy', 'true');
    presetLoading?.removeAttribute('aria-hidden');
  };
  const hidePresetLoading = () => {
    if (!presetPreview) return;
    presetPreview.classList.remove('is-loading');
    presetPreview.setAttribute('aria-busy', 'false');
    presetLoading?.setAttribute('aria-hidden', 'true');
  };
  const setPresetPreview = (card) => {
    if (!presetVideo || !card) return;

    presetCards.forEach(item => {
      const active = item === card;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });

    if (presetVideo.getAttribute('src') !== card.dataset.presetVideo) {
      showPresetLoading();
      presetVideo.pause();
      presetVideo.src = card.dataset.presetVideo;
      presetVideo.setAttribute('aria-label', `${card.dataset.presetName} preset demonstration`);
      presetVideo.load();
      presetVideo.play().catch(() => {});
    }
  };

  if (presetVideo) {
    ['loadstart', 'waiting'].forEach(eventName => {
      presetVideo.addEventListener(eventName, showPresetLoading);
    });
    ['canplay', 'playing'].forEach(eventName => {
      presetVideo.addEventListener(eventName, hidePresetLoading);
    });
    presetVideo.addEventListener('error', hidePresetLoading);
    if (presetVideo.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) hidePresetLoading();
    else showPresetLoading();
  }

  presetCards.forEach((card, index) => {
    card.addEventListener('click', () => setPresetPreview(card));
    card.addEventListener('keydown', (event) => {
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % presetCards.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + presetCards.length) % presetCards.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = presetCards.length - 1;
      else return;

      event.preventDefault();
      setPresetPreview(presetCards[nextIndex]);
      presetCards[nextIndex].focus();
    });
  });

  /* ====== PARALLAX ON HERO KV ====== */
  const heroVisual = document.querySelector('.kv-mouse');
  const heroSection = document.getElementById('hero');
  if (heroVisual && heroSection) {
    heroSection.addEventListener('mousemove', (e) => {
      const r = heroSection.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const x = (e.clientX - r.left - r.width / 2) / r.width * 18;
      const y = (e.clientY - r.top - r.height / 2) / r.height * 18;
      heroVisual.style.transform = `translate3d(${x}px,${y}px,0)`;
    }, { passive: true });
    heroSection.addEventListener('mouseleave', () => {
      heroVisual.style.transform = '';
    });
  }

  /* ====== ACTIVE NAV HIGHLIGHT ====== */
  const navLinks = document.querySelectorAll('.nav-links a');
  const sections = [...navLinks].map(a => document.querySelector(a.getAttribute('href'))).filter(Boolean);
  window.addEventListener('scroll', () => {
    const y = window.scrollY + 120;
    let current = '';
    sections.forEach((sec, i) => {
      if (sec && sec.offsetTop <= y) current = navLinks[i]?.getAttribute('href');
    });
    navLinks.forEach(a => {
      a.style.opacity = a.getAttribute('href') === current ? '1' : '';
    });
  }, { passive: true });
});
